import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { notificationRepository, userRepository } from '@/lib/services';

// Notification Types
export interface BaseNotification {
  id?: string;
  recipientId: string;
  senderId: string;
  type:
    | 'jobApplication'
    | 'jobAcceptance'
    | 'jobRejection'
    | 'message'
    | 'profileView'
    | 'connectRequest'
    | 'connectConfirmed'
    | 'connectCancelled'
    | 'payment'
    | 'purchaseConfirmation'
    | 'saleNotification';
  title: string;
  message: string;
  data?: any; // Additional context data
  actionUrl?: string;
  read: boolean;
  createdAt: Timestamp | string;
  updatedAt: Timestamp | string;
}

function panelRowToBaseNotification(
  row: Record<string, unknown> & { id: string },
): BaseNotification {
  // Two writer shapes exist in production:
  //   1. Newer cart/seller/job triggers write `type`, `data`, `actionUrl`
  //      at the document root.
  //   2. Legacy panel notifications (createJobPanelNotification) wrote
  //      `metadata.panelType` and `metadata.payload`.
  // Read the root fields first and only fall back to `metadata.*` if the
  // root is missing. Previously this mapper read ONLY from metadata, which
  // produced `type: 'message'` for every modern notification — and routed
  // every click to /messages — whenever the realtime listener fell back to
  // this static fetch path.
  const meta = (
    row.metadata && typeof row.metadata === 'object' ? row.metadata : {}
  ) as Record<string, unknown>;
  const rawType =
    typeof row.type === 'string' && row.type
      ? row.type
      : typeof meta.panelType === 'string'
        ? meta.panelType
        : 'message';
  const rawData =
    row.data && typeof row.data === 'object'
      ? row.data
      : meta.payload && typeof meta.payload === 'object'
        ? meta.payload
        : undefined;
  const read =
    typeof row.read === 'boolean'
      ? row.read
      : typeof row.isRead === 'boolean'
        ? row.isRead
        : false;

  return {
    id: row.id,
    recipientId:
      typeof row.recipientId === 'string'
        ? row.recipientId
        : typeof row.userId === 'string'
          ? row.userId
          : '',
    senderId: typeof row.senderId === 'string' ? row.senderId : '',
    type: rawType as BaseNotification['type'],
    title: String(row.title ?? ''),
    message: String(row.message ?? ''),
    data: rawData,
    actionUrl: typeof row.actionUrl === 'string' ? row.actionUrl : undefined,
    read,
    createdAt: row.createdAt as Timestamp,
    updatedAt: row.updatedAt as Timestamp,
  };
}

export interface JobApplicationNotification extends BaseNotification {
  type: 'jobApplication';
  data: {
    jobId: string;
    jobTitle: string;
    applicantProfileId: string;
    applicantName: string;
    applicantEmail: string;
  };
}

export interface JobResponseNotification extends BaseNotification {
  type: 'jobAcceptance' | 'jobRejection';
  data: {
    jobId: string;
    jobTitle: string;
    response: 'accepted' | 'rejected';
    message?: string;
  };
}

export interface ConnectNotification extends BaseNotification {
  type: 'connectRequest' | 'connectConfirmed' | 'connectCancelled';
  data: {
    requestId: string;
    instructorName: string;
    clientName: string;
    serviceType: string;
    requestDate: string;
    message?: string;
  };
}

// Notification Service Interface
export interface INotificationService {
  sendNotification(notification: Omit<BaseNotification, 'id'>): Promise<string>;
  getNotifications(userId: string, unreadOnly?: boolean): Promise<BaseNotification[]>;
  markAsRead(notificationId: string): Promise<void>;
  markAllAsRead(userId: string): Promise<void>;
}

// Email Notification Service
export interface IEmailNotificationService {
  sendJobApplicationEmail(
    recipientEmail: string,
    jobTitle: string,
    applicantName: string,
    message?: string
  ): Promise<void>;

  sendJobResponseEmail(
    recipientEmail: string,
    jobTitle: string,
    response: 'accepted' | 'rejected',
    message?: string
  ): Promise<void>;
}

// In-App Notification Service Implementation
class InAppNotificationService implements INotificationService {
  private notificationsCollection = collection(db, 'notifications');

  async sendNotification(notification: Omit<BaseNotification, 'id'>): Promise<string> {
    try {
      const id = await notificationRepository.createJobPanelNotification({
        recipientId: notification.recipientId,
        senderId: notification.senderId,
        title: notification.title,
        message: notification.message,
        panelType: notification.type,
        data: notification.data,
        actionUrl: notification.actionUrl,
        read: notification.read,
      });
      console.log('In-app notification sent:', id);
      return id;
    } catch (error) {
      // Cross-user notification writes ARE rejected by Firestore rules
      // (`request.resource.data.userId == request.auth.uid`) — that's
      // expected, the server-side `notifyOn*` triggers handle the cross-
      // user fanout via admin SDK. Demote permission-denied to a warning
      // so the consoleErrorBridge doesn't ship every "I sent X to another
      // user" event to Sentry as an error. Other failure modes (network,
      // schema) still surface as errors. The underlying FirebaseError
      // gets wrapped in a DRXError, so the rule denial code lives on
      // `error.cause.code` not `error.code`.
      const directCode = (error as { code?: string } | null)?.code || '';
      const causeCode = ((error as { cause?: { code?: string } } | null)?.cause?.code) || '';
      const isPermissionDenied = directCode === 'permission-denied' || causeCode === 'permission-denied';
      if (isPermissionDenied) {
        console.warn('In-app notification fanout deferred to server trigger (cross-user write blocked).');
      } else {
        console.error('Error sending in-app notification:', error);
      }
      throw error;
    }
  }

  async getNotifications(userId: string, unreadOnly = false): Promise<BaseNotification[]> {
    try {
      const rows = await notificationRepository.getPanelNotificationsForUser(
        userId,
        unreadOnly,
        50,
      );
      return rows.map(panelRowToBaseNotification);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  async markAsRead(notificationId: string): Promise<void> {
    try {
      await notificationRepository.markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(userId: string): Promise<void> {
    try {
      await notificationRepository.markAllNotificationsAsRead(userId);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Real-time inbox subscription. Filters on `userId` because that is the
  // canonical recipient field — Firestore rules require
  // `resource.data.userId == auth.uid` for both read and list, and every
  // server-side fanout trigger (notify-on-favorite, notify-on-job-posted,
  // notify-on-job-application) writes `userId`. Filtering on the legacy
  // `recipientId` field instead made Firestore reject the listener with
  // "Missing or insufficient permissions" because rules can't statically
  // prove a `recipientId`-filtered query satisfies a `userId`-based rule.
  // The output still exposes a `recipientId` field on each row for legacy
  // consumers — falls back to `userId` when the doc only has the canonical
  // field (trigger-written docs).
  subscribeToNotifications(
    userId: string,
    callback: (notifications: BaseNotification[]) => void
  ): () => void {
    // The newest 50 (userId + createdAt index). Without the order it was
    // any 50, so with more than 50 a new notification could be missing.
    const notificationQuery = query(
      this.notificationsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      notificationQuery,
      (querySnapshot) => {
        const notifications = querySnapshot.docs.map((doc) => {
          const data = doc.data() as Record<string, unknown>;
          return {
            id: doc.id,
            ...data,
            recipientId:
              typeof data.recipientId === 'string' && data.recipientId
                ? data.recipientId
                : typeof data.userId === 'string'
                  ? data.userId
                  : '',
          };
        }) as BaseNotification[];

        notifications.sort((a, b) => {
          const aTime = (a.createdAt as Timestamp | undefined)?.seconds || 0;
          const bTime = (b.createdAt as Timestamp | undefined)?.seconds || 0;
          return bTime - aTime;
        });

        callback(notifications);
      },
      (error) => {
        console.error('❌ Error in notifications subscription:', error);
        callback([]);
      }
    );

    return unsubscribe;
  }
}

// Email Notification Service Implementation (Mock for now)
class EmailNotificationService implements IEmailNotificationService {
  async sendJobApplicationEmail(
    recipientEmail: string,
    jobTitle: string,
    applicantName: string,
    message?: string
  ): Promise<void> {
    try {
      // TODO: Implement actual email sending (SendGrid, AWS SES, etc.)
      console.log('📧 Sending job application email:', {
        to: recipientEmail,
        subject: `New Job Application: ${jobTitle}`,
        applicant: applicantName,
        message
      });

      // Mock email implementation
      // In production, this would integrate with your email service
    } catch (error) {
      console.error('❌ Error sending job application email:', error);
      throw error;
    }
  }

  async sendJobResponseEmail(
    recipientEmail: string,
    jobTitle: string,
    response: 'accepted' | 'rejected',
    message?: string
  ): Promise<void> {
    try {
      console.log('📧 Sending job response email:', {
        to: recipientEmail,
        subject: `Job Application ${response}: ${jobTitle}`,
        response,
        message
      });

      // Mock email implementation
    } catch (error) {
      console.error('❌ Error sending job response email:', error);
      throw error;
    }
  }
}

// Main Notification Service that orchestrates both in-app and email
class NotificationService {
  private inAppService: InAppNotificationService;
  private emailService: EmailNotificationService;
  private sendNotificationEmailFunction = httpsCallable(functions, 'sendNotificationEmail');
  private sendJobApplicationEmailFunction = httpsCallable(functions, 'sendJobApplicationEmail');
  private sendMessageNotificationEmailFunction = httpsCallable(functions, 'sendMessageNotificationEmail');

  constructor() {
    this.inAppService = new InAppNotificationService();
    this.emailService = new EmailNotificationService();
  }

  // Helper function to get user email and name
  private async getUserDetails(userId: string): Promise<{ email?: string; name?: string }> {
    try {
      const userData = await userRepository.getUserById(userId);
      return {
        email: userData?.email,
        name: userData?.firstName || userData?.username || 'User',
      };
    } catch (error) {
      console.error('Error getting user details for email:', error);
      return {};
    }
  }

  // Send email notification for any notification type
  private async sendEmailNotification(notification: BaseNotification): Promise<void> {
    try {
      const userDetails = await this.getUserDetails(notification.recipientId);

      if (!userDetails.email) {
        console.log('No email found for user, skipping email notification');
        return;
      }

      // Use the comprehensive notification email function
      await this.sendNotificationEmailFunction({
        recipientEmail: userDetails.email,
        recipientName: userDetails.name || 'User',
        notificationType: notification.type,
        title: notification.title,
        message: notification.message,
        data: notification.data || {}
      });

      console.log('✅ Email notification sent successfully');
    } catch (error) {
      console.error('❌ Error sending email notification:', error);
      // Don't throw error - we don't want email failures to break in-app notifications
    }
  }

  // Send job application notification
  async sendJobApplication(
    jobId: string,
    jobTitle: string,
    jobCreatorId: string,
    jobCreatorEmail: string,
    applicantId: string,
    applicantName: string,
    applicantEmail: string,
    applicantProfileId: string,
    customMessage?: string
  ): Promise<string> {
    try {
      const message = customMessage || `${applicantName} has applied for your job: ${jobTitle}`;

      // Create notification object
      const notification: Omit<BaseNotification, 'id'> = {
        recipientId: jobCreatorId,
        senderId: applicantId,
        type: 'jobApplication',
        title: `New Job Application`,
        message,
        data: {
          jobId,
          jobTitle,
          applicantProfileId,
          applicantName,
          applicantEmail,
        },
        read: false,
        createdAt: '',
        updatedAt: '',
      };

      // Send in-app notification
      const notificationId = await this.inAppService.sendNotification(notification);

      // Send email notification using dedicated job application email function
      try {
        const jobCreatorDetails = await this.getUserDetails(jobCreatorId);
        if (jobCreatorDetails.email) {
          await this.sendJobApplicationEmailFunction({
            recipientEmail: jobCreatorDetails.email,
            recipientName: jobCreatorDetails.name || 'User',
            jobTitle,
            jobId,
            applicantName,
            applicantEmail,
            applicationMessage: customMessage
          });
        }
      } catch (emailError) {
        console.error('❌ Error sending job application email:', emailError);
        // Don't fail the whole process if email fails
      }

      console.log('✅ Job application notification sent successfully');
      return notificationId;
    } catch (error) {
      const directCode = (error as { code?: string } | null)?.code || '';
      const causeCode = ((error as { cause?: { code?: string } } | null)?.cause?.code) || '';
      const isPermissionDenied = directCode === 'permission-denied' || causeCode === 'permission-denied';
      if (isPermissionDenied) {
        console.warn('❌ Job application in-app notification deferred to server trigger.');
      } else {
        console.error('❌ Error sending job application notification:', error);
      }
      throw error;
    }
  }

  // Send job response notification (accept/reject)
  async sendJobResponse(
    jobId: string,
    jobTitle: string,
    applicantId: string,
    applicantEmail: string,
    response: 'accepted' | 'rejected',
    senderId: string,
    customMessage?: string
  ): Promise<string> {
    try {
      const message = customMessage ||
        `Your application for "${jobTitle}" has been ${response}`;

      // Create notification object
      const notification: Omit<BaseNotification, 'id'> = {
        recipientId: applicantId,
        senderId,
        type: response === 'accepted' ? 'jobAcceptance' : 'jobRejection',
        title: `Job Application ${response}`,
        message,
        data: {
          jobId,
          jobTitle,
          response,
          message: customMessage,
        },
        read: false,
        createdAt: '',
        updatedAt: '',
      };

      // Send in-app notification
      const notificationId = await this.inAppService.sendNotification(notification);

      // Send email notification using the existing job notification function
      try {
        const applicantDetails = await this.getUserDetails(applicantId);
        const senderDetails = await this.getUserDetails(senderId);

        if (applicantDetails.email) {
          // Use the existing sendJobNotificationEmail function
          const sendJobNotificationEmailFunction = httpsCallable(functions, 'sendJobNotificationEmail');
          await sendJobNotificationEmailFunction({
            applicantEmail: applicantDetails.email,
            applicantName: applicantDetails.name || 'User',
            jobTitle,
            jobId,
            employerName: senderDetails.name || 'Employer',
            notificationType: response === 'accepted' ? 'acceptance' : 'rejection',
            customMessage
          });
        }
      } catch (emailError) {
        console.error('❌ Error sending job response email:', emailError);
        // Don't fail the whole process if email fails
      }

      return notificationId;
    } catch (error) {
      console.error('❌ Error sending job response notification:', error);
      throw error;
    }
  }

  // Get notifications for user
  async getNotifications(userId: string, unreadOnly = false): Promise<BaseNotification[]> {
    return this.inAppService.getNotifications(userId, unreadOnly);
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    return this.inAppService.markAsRead(notificationId);
  }

  // Mark all notifications as read
  async markAllAsRead(userId: string): Promise<void> {
    return this.inAppService.markAllAsRead(userId);
  }

  // Send connect request notification (in-app)
  async sendConnectRequest(
    requestId: string,
    instructorId: string,
    clientId: string,
    clientName: string,
    instructorName: string,
    serviceType: string,
    requestDate: string,
    customMessage?: string
  ): Promise<string> {
    try {
      const message = customMessage || `${clientName} has requested your ${serviceType} for ${new Date(requestDate).toLocaleDateString()}`;

      const notificationId = await this.inAppService.sendNotification({
        recipientId: instructorId,
        senderId: clientId,
        type: 'connectRequest',
        title: 'New Request',
        message,
        data: {
          requestId,
          instructorName,
          clientName,
          serviceType,
          requestDate,
          message: customMessage,
        },
        read: false,
        createdAt: '',
        updatedAt: '',
      });

      console.log('✅ Connect request notification sent successfully');
      return notificationId;
    } catch (error) {
      console.error('❌ Error sending connect request notification:', error);
      throw error;
    }
  }

  async sendConnectConfirmed(
    requestId: string,
    clientId: string,
    instructorId: string,
    instructorName: string,
    clientName: string,
    serviceType: string,
    requestDate: string,
    customMessage?: string
  ): Promise<string> {
    try {
      const message = customMessage || `Your session with ${instructorName} for ${serviceType} on ${new Date(requestDate).toLocaleDateString()} has been confirmed!`;

      const notificationId = await this.inAppService.sendNotification({
        recipientId: clientId,
        senderId: instructorId,
        type: 'connectConfirmed',
        title: 'Request Confirmed',
        message,
        data: {
          requestId,
          instructorName,
          clientName,
          serviceType,
          requestDate,
          message: customMessage,
        },
        read: false,
        createdAt: '',
        updatedAt: '',
      });

      console.log('✅ Connect confirmation notification sent successfully');
      return notificationId;
    } catch (error) {
      console.error('❌ Error sending connect confirmation notification:', error);
      throw error;
    }
  }

  async sendConnectCancelled(
    requestId: string,
    recipientId: string,
    senderId: string,
    instructorName: string,
    clientName: string,
    serviceType: string,
    requestDate: string,
    customMessage?: string
  ): Promise<string> {
    try {
      const message = customMessage || `The ${serviceType} session on ${new Date(requestDate).toLocaleDateString()} has been cancelled`;

      const notificationId = await this.inAppService.sendNotification({
        recipientId,
        senderId,
        type: 'connectCancelled',
        title: 'Request Cancelled',
        message,
        data: {
          requestId,
          instructorName,
          clientName,
          serviceType,
          requestDate,
          message: customMessage,
        },
        read: false,
        createdAt: '',
        updatedAt: '',
      });

      console.log('✅ Connect cancellation notification sent successfully');
      return notificationId;
    } catch (error) {
      console.error('❌ Error sending connect cancellation notification:', error);
      throw error;
    }
  }

  // Real-time subscription to notifications
  subscribeToNotifications(
    userId: string,
    callback: (notifications: BaseNotification[]) => void
  ): () => void {
    return this.inAppService.subscribeToNotifications(userId, callback);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;